import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { adminApi, type Achievement } from '@/api';

export function AchievementForm() {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Achievement>({
    id: '',
    title: '',
    description: '',
    icon: 'Target',
    color: 'emerald',
    requirement: '',
    requirementType: 'lesson_count',
    requirementConfig: { min: 1 },
    reward: 0,
  });

  useEffect(() => {
    if (isNew) return;
    adminApi.achievements.get(id!).then(setForm).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isNew) {
        await adminApi.achievements.create(form);
        navigate('/achievements');
      } else {
        await adminApi.achievements.update(id!, form);
        navigate('/achievements');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <Link to="/achievements" className="text-slate-600 hover:text-slate-900 text-sm">
          ← Achievements
        </Link>
        <h1 className="text-2xl font-semibold text-slate-800">{isNew ? 'New achievement' : 'Edit achievement'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ID</label>
          <input
            value={form.id}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
            required
            disabled={!isNew}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100"
            placeholder="e.g. first-sync"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            required
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Icon</label>
            <input
              value={form.icon}
              onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="e.g. Target"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
            <input
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="e.g. emerald"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Requirement</label>
          <input
            value={form.requirement}
            onChange={(e) => setForm((f) => ({ ...f, requirement: e.target.value }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
            placeholder="e.g. Complete 1 lesson"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Requirement Type</label>
          <select
            value={form.requirementType}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                requirementType: e.target.value as Achievement['requirementType'],
              }))
            }
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          >
            <option value="lesson_count">Lesson count</option>
            <option value="track_complete">Track complete</option>
            <option value="streak_days">Streak days</option>
            <option value="xp_total">XP total</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Requirement Config (JSON)</label>
          <textarea
            value={JSON.stringify(form.requirementConfig, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value || '{}');
                setForm((f) => ({ ...f, requirementConfig: parsed }));
              } catch {
                // Invalid JSON, ignore
              }
            }}
            required
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
            placeholder='e.g. {"min": 1} or {"trackId": "ethics-safety"}'
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reward (XP)</label>
          <input
            type="number"
            min={0}
            value={form.reward}
            onChange={(e) => setForm((f) => ({ ...f, reward: Number(e.target.value) }))}
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        {error && <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to="/achievements" className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
